/**
 * Protected route wrapper that requires authentication.
 */

import { useAuth0 } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";

import { Loader } from "@/ui/atoms";

import { getAuth0Config } from "./config";
import { tokenService } from "./tokenService";

interface TProtectedRouteProps {
    children: ReactNode;
}

export function ProtectedRoute({ children }: TProtectedRouteProps) {
    const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout } =
        useAuth0();
    const config = getAuth0Config();
    // Track if we're already handling an auth error to prevent multiple redirects
    const handlingAuthError = useRef(false);

    // Handler for auth errors (401 responses or token refresh failures)
    const handleAuthError = useCallback(() => {
        if (handlingAuthError.current) {
            return;
        }
        handlingAuthError.current = true;

        console.warn("Auth error detected, redirecting to login...");

        // Clear auth state and redirect to login
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        });
    }, [logout]);

    // Set up the token getter and auth error handler for the API client.
    // This allows the API interceptor to get fresh tokens on every request
    // and handle auth failures by triggering re-login.
    useEffect(() => {
        if (isAuthenticated) {
            // Set up token getter
            tokenService.setTokenGetter(async () => {
                try {
                    return await getAccessTokenSilently({
                        authorizationParams: {
                            audience: config.audience,
                        },
                    });
                } catch (err) {
                    console.error("Failed to get access token:", err);
                    // Token refresh failed - trigger re-login
                    handleAuthError();
                    return null;
                }
            });

            // Set up auth error handler for 401 responses
            tokenService.setAuthErrorHandler(handleAuthError);
        } else {
            tokenService.clearTokenGetter();
            tokenService.clearAuthErrorHandler();
        }

        return () => {
            // Cleanup on unmount
            tokenService.clearAuthErrorHandler();
        };
    }, [isAuthenticated, getAccessTokenSilently, config.audience, handleAuthError]);

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader size="lg" />
            </div>
        );
    }

    if (!isAuthenticated) {
        // Redirect to login
        loginWithRedirect({
            appState: { returnTo: window.location.pathname },
        });
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader size="lg" />
            </div>
        );
    }

    return <>{children}</>;
}
